import type { Ctx } from "@milkdown/ctx";
import type { Node as ProseNode } from "@milkdown/prose/model";
import type { EditorView, NodeView } from "@milkdown/prose/view";
import { Plugin } from "@milkdown/prose/state";
import { Decoration, DecorationSet } from "@milkdown/prose/view";
import { TextSelection } from "@milkdown/prose/state";
import { commandsCtx } from "@milkdown/core";
import {
  $command,
  $ctx,
  $nodeSchema,
  $prose,
  $remark,
  $view,
} from "@milkdown/utils";
import remarkDirective from "remark-directive";
import { visit } from "unist-util-visit";
import { createRoot, type Root } from "react-dom/client";
import { StrictMode } from "react";
import { VariableChip } from "./VariableChip";
import { normalizeVariableRefId } from "@/components/SpotflowV2/Sidebar/variableRefs";
import { normalizeVarDirectives } from "./normalizeVarDirectives";

interface DirectiveNode {
  type: "textDirective" | "leafDirective" | "containerDirective";
  name?: string;
  attributes?: Record<string, string | undefined>;
  children?: Array<{ type: string; value?: string }>;
  value?: string;
}

// ── ctx slice: lets the plugin know which editor instance it belongs to ─────
export const editorIdCtx = $ctx<string, "editorId">("", "editorId");

// ── PM node: inline atom, one attribute (the stable input id) ───────────────
export const variableSchema = $nodeSchema("variable", () => ({
  inline: true,
  group: "inline",
  atom: true,
  selectable: true,
  draggable: false,
  marks: "",
  attrs: {
    id: { default: "" },
  },
  parseDOM: [
    {
      tag: "span[data-variable-id]",
      getAttrs: (dom: HTMLElement | string) => {
        if (typeof dom === "string") return false;
        return { id: dom.getAttribute("data-variable-id") ?? "" };
      },
    },
  ],
  toDOM: (node: ProseNode) => [
    "span",
    {
      "data-variable-id": node.attrs.id as string,
      class: "variable-chip",
    },
  ],
  parseMarkdown: {
    match: (node) =>
      (node.type === "textDirective" || node.type === "leafDirective") &&
      node.name === "var",
    runner: (state, node, type) => {
      const children = (node.children ?? []) as Array<{ value?: string }>;
      const id = normalizeVariableRefId(
        children.map((c) => c.value ?? "").join("")
      );
      state.addNode(type, { id });
    },
  },
  toMarkdown: {
    match: (node) => node.type.name === "variable",
    runner: (state, node) => {
      state.addNode("textDirective", undefined, undefined, {
        name: "var",
        children: [{ type: "text", value: node.attrs.id as string }],
      });
    },
  },
}));

// ── remark plugin: parses `:var[input1]` ────────────────────────────────────
export const directiveRemark = $remark(
  "remarkDirective",
  () => remarkDirective
);

// ── remark plugin: downgrade non-`var` directives to plain text. ────────────
// remark-directive aggressively consumes patterns like `:D`, `:foo`, `::bar`,
// so any stray colon in a user's prose would crash the mdast → PM conversion
// (no matching node type). Walk the tree post-parse and reconstruct the
// original source for every non-`var` directive so milkdown sees plain text.
function reconstructDirectiveSource(node: DirectiveNode): string {
  const marker =
    node.type === "containerDirective"
      ? ":::"
      : node.type === "leafDirective"
        ? "::"
        : ":";
  let out = marker + (node.name ?? "");
  const childText = (node.children ?? []).map((c) => c.value ?? "").join("");
  if (childText) out += `[${childText}]`;
  const attrs = node.attributes ?? {};
  const attrKeys = Object.keys(attrs);
  if (attrKeys.length > 0) {
    const parts = attrKeys.map((k) => {
      const v = attrs[k];
      return v == null || v === "" ? k : `${k}="${v}"`;
    });
    out += `{${parts.join(" ")}}`;
  }
  return out;
}

export const normalizeVarDirectivesRemark = $remark(
  "normalizeVarDirectives",
  () => normalizeVarDirectives
);

export const stripUnknownDirectivesRemark = $remark(
  "stripUnknownDirectives",
  () => () => (tree: unknown) => {
    visit(tree as Parameters<typeof visit>[0], (node) => {
      const n = node as DirectiveNode;
      if (
        n.type !== "textDirective" &&
        n.type !== "leafDirective" &&
        n.type !== "containerDirective"
      ) {
        return;
      }
      if (n.name === "var") return;
      const textValue = reconstructDirectiveSource(n);
      const asText = n as unknown as { type: string; value: string };
      asText.type = "text";
      asText.value = textValue;
      delete n.name;
      delete n.attributes;
      delete n.children;
    });
  }
);

// ── NodeView: mounts a React root rendering VariableChip with live updates ──
class VariableNodeView implements NodeView {
  dom: HTMLElement;
  private root: Root;
  private editorId: string;
  private currentId: string;

  constructor(node: ProseNode, editorId: string) {
    this.editorId = editorId;
    this.currentId = (node.attrs.id as string) ?? "";
    this.dom = document.createElement("span");
    this.dom.setAttribute("data-variable-id", this.currentId);
    this.dom.className = "variable-chip";
    this.dom.contentEditable = "false";
    this.root = createRoot(this.dom);
    this.renderChip();
  }

  private renderChip() {
    this.root.render(
      <StrictMode>
        <VariableChip editorId={this.editorId} inputId={this.currentId} />
      </StrictMode>
    );
  }

  update(node: ProseNode): boolean {
    if (node.type.name !== "variable") return false;
    const nextId = (node.attrs.id as string) ?? "";
    if (nextId !== this.currentId) {
      this.currentId = nextId;
      this.dom.setAttribute("data-variable-id", nextId);
      this.renderChip();
    }
    return true;
  }

  selectNode() {
    this.dom.classList.add("variable-chip-selected");
  }

  deselectNode() {
    this.dom.classList.remove("variable-chip-selected");
  }

  destroy() {
    queueMicrotask(() => this.root.unmount());
  }

  // Let PM handle clicks so clicking the chip puts a NodeSelection on it.
  // Mutations inside the chip come from React rendering — PM must ignore.
  ignoreMutation() {
    return true;
  }
}

export const variableView = $view(
  variableSchema.node,
  (ctx: Ctx) => (node: ProseNode, _view: EditorView) =>
    new VariableNodeView(node, ctx.get(editorIdCtx.key))
);

// Both chip node types share the `variable-chip` DOM class, so selection
// styling and the drag ghost treat them uniformly.
const CHIP_NODE_TYPES = new Set(["variable", "template"]);

// Highlight chips covered by a TextSelection range (Cmd+A, drag-select, etc).
// NodeSelection is already covered by selectNode/deselectNode on the NodeView.
export const variableSelectionHighlight = $prose(
  () =>
    new Plugin({
      props: {
        decorations(state) {
          const { selection } = state;
          if (selection.empty) return null;
          const decorations: Decoration[] = [];
          state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            if (!CHIP_NODE_TYPES.has(node.type.name)) return;
            // Only decorate when the whole chip is inside the range.
            if (pos < selection.from || pos + node.nodeSize > selection.to)
              return;
            decorations.push(
              Decoration.node(pos, pos + node.nodeSize, {
                class: "variable-chip-selected",
              })
            );
          });
          return decorations.length
            ? DecorationSet.create(state.doc, decorations)
            : null;
        },
      },
    })
);

// The browser's default drag ghost is a faint snapshot of the chip's bare DOM
// (no background/border), which is nearly invisible while dragging. Replace it
// with a styled clone so the dragged chip reads clearly. PM's built-in
// dragstart never sets a drag image, and handleDOMEvents runs before it, so
// returning false here lets PM still wire up `view.dragging` for the drop.
export const variableDragImage = $prose(
  () =>
    new Plugin({
      props: {
        handleDOMEvents: {
          dragstart: (_view, event) => {
            const dt = event.dataTransfer;
            if (!dt) return false;
            const target = event.target as HTMLElement | null;
            const chip = target?.closest?.(
              ".variable-chip"
            ) as HTMLElement | null;
            if (!chip) return false;

            const rect = chip.getBoundingClientRect();
            const ghost = chip.cloneNode(true) as HTMLElement;
            ghost.classList.remove("variable-chip-selected");
            ghost.classList.add("variable-chip-drag-ghost");
            ghost.style.position = "fixed";
            ghost.style.top = "-1000px";
            ghost.style.left = "-1000px";
            ghost.style.pointerEvents = "none";
            document.body.appendChild(ghost);

            dt.setDragImage(
              ghost,
              event.clientX - rect.left,
              event.clientY - rect.top
            );
            window.setTimeout(() => ghost.remove(), 0);
            return false;
          },
        },
      },
    })
);

export const insertVariableCommand = $command(
  "InsertVariable",
  (ctx) => (payload?: { id: string }) => (state, dispatch) => {
    const id = payload?.id ?? "";
    if (!id) return false;
    if (!dispatch) return true;
    const chip = variableSchema.type(ctx).create({ id });
    const space = state.schema.text(" ");
    let tr = state.tr.replaceSelectionWith(chip);
    const afterChip = tr.selection.from;
    tr = tr.insert(afterChip, space);
    const cursorPos = afterChip + space.nodeSize;
    tr = tr.setSelection(TextSelection.create(tr.doc, cursorPos));
    dispatch(tr.scrollIntoView());
    return true;
  }
);

export function insertVariable(ctx: Ctx, id: string) {
  ctx.get(commandsCtx).call(insertVariableCommand.key, { id });
}

export const variablePlugin = [
  editorIdCtx,
  variableSchema,
  directiveRemark,
  stripUnknownDirectivesRemark,
  normalizeVarDirectivesRemark,
  variableView,
  variableSelectionHighlight,
  variableDragImage,
  insertVariableCommand,
].flat();
